'use client';

import React from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log the error with comprehensive details
    console.error('[ErrorBoundary] React component error caught:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      timestamp: new Date().toISOString()
    });

    // Also log to any external error tracking service if available
    if (typeof window !== 'undefined') {
      try {
        const mixpanel = require('../lib/mixpanel').default;
        if (mixpanel) {
          mixpanel.track('error_boundary_triggered', {
            error_message: error.message,
            component_stack: errorInfo.componentStack,
            error_stack: error.stack,
            url: window.location.href
          });
        }
      } catch (trackingError) {
        console.error('[ErrorBoundary] Failed to track error:', trackingError);
      }
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback;
      return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// Default fallback component
const DefaultErrorFallback: React.FC<{ error?: Error; resetError: () => void }> = ({ error, resetError }) => {
  return (
    <div style={{
      padding: '20px',
      border: '1px solid #e74c3c',
      borderRadius: '8px',
      backgroundColor: '#fdf2f2',
      margin: '20px',
      textAlign: 'center'
    }}>
      <h2 style={{ color: '#e74c3c', marginBottom: '10px' }}>Something went wrong</h2>
      <p style={{ color: '#721c24', marginBottom: '15px' }}>
        We're sorry, but there was an error loading this component.
      </p>
      {error && (
        <details style={{ marginBottom: '15px', textAlign: 'left' }}>
          <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>Error Details</summary>
          <pre style={{ 
            background: '#f8f8f8', 
            padding: '10px', 
            borderRadius: '4px',
            fontSize: '12px',
            overflow: 'auto',
            marginTop: '10px'
          }}>
            {error.message}
          </pre>
        </details>
      )}
      <button 
        onClick={resetError}
        style={{
          background: '#e74c3c',
          color: 'white',
          border: 'none',
          padding: '10px 20px',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        Try Again
      </button>
    </div>
  );
};

export default ErrorBoundary; 